import React from 'react';
import { Receipt } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import { LUNCH, DINNER, PREV, shortDate, weekday, mergePrev } from './CoversByDayChart';

const TOTAL = '#111827';
const LABELS = { lunch_ticket: 'Midi', dinner_ticket: 'Soir', total_ticket: 'Tous services', prev_total_ticket: 'Sem. précédente (tous)' };
const chf = (v) => (v == null ? '—' : `${v.toFixed(2)} CHF`);

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs" data-testid="ticket-chart-tooltip">
      <p className="font-semibold text-gray-900 mb-1">{weekday(label)} {shortDate(label)}</p>
      <p style={{ color: LUNCH }}>Midi : <b>{chf(row.lunch_ticket)}</b>{row.lunch_covers ? ` · ${row.lunch_covers} convives` : ' · fermé'}</p>
      <p style={{ color: DINNER }}>Soir : <b>{chf(row.dinner_ticket)}</b>{row.dinner_covers ? ` · ${row.dinner_covers} convives` : ' · fermé'}</p>
      <p className="text-gray-700 mt-1">Tous services : <b>{chf(row.total_ticket)}</b></p>
      {row.prev_date && <p className="text-gray-400">Sem. précédente ({shortDate(row.prev_date)}) : {chf(row.prev_total_ticket)} · midi {chf(row.prev_lunch_ticket)} · soir {chf(row.prev_dinner_ticket)}</p>}
    </div>
  );
};

export const AvgTicketByDayChart = ({ byDay, byDayPrev, service }) => {
  if (!byDay?.length) return null;
  const data = mergePrev(byDay, byDayPrev);
  const hasPrev = data.some(d => d.prev_date);
  const dim = (svc) => (service && service !== svc ? 0.25 : 1);
  const avg = (key) => { const v = byDay.map(d => d[key]).filter(x => x != null); return v.length ? (v.reduce((a, b) => a + b, 0) / v.length).toFixed(0) : '—'; };
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-5" data-testid="ticket-by-day-chart">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-gray-700">
          <Receipt size={16} className="text-emerald-600" />
          <span className="text-xs font-bold uppercase tracking-wider">Ticket moyen par jour (CHF) · midi / soir</span>
        </div>
        <p className="text-xs text-gray-400" data-testid="ticket-chart-summary">Moyenne des jours : midi <b style={{ color: LUNCH }}>{avg('lunch_ticket')} CHF</b> · soir <b style={{ color: DINNER }}>{avg('dinner_ticket')} CHF</b></p>
      </div>
      <div className="h-56 md:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 14, right: 8, left: 0, bottom: 4 }}>
            <CartesianGrid vertical={false} stroke="#f3f4f6" />
            <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} interval={data.length > 14 ? Math.ceil(data.length / 10) - 1 : 0} />
            <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} width={34} />
            <Tooltip content={<TooltipBox />} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} formatter={(v) => LABELS[v] || v} />
            <Line type="monotone" dataKey="lunch_ticket" stroke={LUNCH} strokeOpacity={dim('lunch')} strokeWidth={2.5} dot={{ r: 3, strokeWidth: 2, fill: '#fff' }} connectNulls isAnimationActive={false} />
            <Line type="monotone" dataKey="dinner_ticket" stroke={DINNER} strokeOpacity={dim('dinner')} strokeWidth={2.5} dot={{ r: 3, strokeWidth: 2, fill: '#fff' }} connectNulls isAnimationActive={false} />
            <Line type="monotone" dataKey="total_ticket" stroke={TOTAL} strokeOpacity={service ? 0.25 : 0.8} strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false} />
            {hasPrev && <Line type="monotone" dataKey="prev_total_ticket" stroke={PREV} strokeWidth={2} strokeDasharray="6 4" dot={false} connectNulls isAnimationActive={false} />}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AvgTicketByDayChart;
